import React, { useMemo, useState, useEffect } from "react"

import {
  Container,
  CardBody,
  Card,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import { axiosbaseurl } from "configure"
import moment from "moment"

import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import CommonTable from "components/Common/CommonTable"

import * as XLSX from "xlsx"
import swal from "sweetalert"
import Loader from "components/Common/Loader"

const ActiveTaskLastLog = () => {
  const [table_data, settable_data] = useState([])
  const [logsModal, setLogsModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedLogs, setSelectedLogs] = useState({
    workDescription: "",
    createdAt: "",
  })

  const toggleLogsModal = () => setLogsModal(!logsModal)

  const getLogsData = async () => {
    setLoading(true)
    try {
      const response = await axiosbaseurl.get(`/reports/latest/tasks/log`)
      if (response.data.success) {
        settable_data(response.data.data)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getLogsData()
  }, [])

  const handleExportToExcel = () => {
    if (!table_data.length) {
      swal("", "No task logs available to export", "warning")
      return
    }

    // Prepare export data with all displayed fields
    const exportData = table_data.map((item, index) => ({
      ["#"]: index + 1,
      ["Client Name"]: item?.client?.clientName || "--",
      ["Task Name"]: item?.task?.title || "--",
      ["Time Spent"]: `${item?.totalTimeSpent} hrs` || "--",
      ["Period End"]: item?.task?.startDate
        ? moment(item.task.startDate).format("DD/MM/YYYY")
        : "--",
      ["Due Date"]: item?.task?.dueDate
        ? moment(item.task.dueDate).format("DD/MM/YYYY")
        : "--",
      ["Employee Name"]:
        `${item?.employee?.name || ""} ${
          item?.employee?.lastName || ""
        }`.trim() || "--",
      ["Work Type"]: item?.workType?.workType || "--",
      ["Status"]: item?.status?.statusName || "--",
      ["End Time"]: item?.endTime || "--",
      ["Work Description"]:
        item?.workDescription
          ?.replace(/<[^>]+>/g, "") // remove HTML tags for clean Excel text
          .trim() || "--",
    }))

    // Create worksheet starting from row 4
    const ws = XLSX.utils.json_to_sheet(exportData, { origin: "A4" })

    // Add header info (report title and generated timestamp)
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [
          "",
          "Latest Task Report", // B1
          `Generated On: ${moment().format("DD-MM-YYYY HH:mm A")}`, // C1
        ],
      ],
      { origin: "A1" }
    )

    // Auto-adjust column widths dynamically
    const colWidths = Object.keys(exportData[0]).map(key => ({
      wch: Math.max(
        key.length,
        ...exportData.map(row => String(row[key] || "").length)
      ),
    }))
    ws["!cols"] = colWidths

    // Create workbook and append worksheet
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Latest_Task_Report")

    // Save Excel file
    XLSX.writeFile(
      wb,
      `Latest_Task_Report-(${moment().format("DD-MM-YYYY_HH-mm-ss")}).xlsx`
    )
  }

  document.title = "Latest Task Report | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}
      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb
            title={"Dashboard"}
            breadcrumbItem={"Latest Task Report"}
          />
          <Card className="px-0 mx-0">
            <CardBody>
              <TableComponent
                table_data={table_data}
                getLogsData={getLogsData}
                handleExportToExcel={handleExportToExcel}
                toggleLogsModal={toggleLogsModal}
                setSelectedLogs={setSelectedLogs}
              />
            </CardBody>
          </Card>

          <Modal
            isOpen={logsModal}
            toggle={toggleLogsModal}
            size="lg"
            centered={true}
          >
            <ModalHeader toggle={toggleLogsModal}>
              <span className="fw-bold text-primary">Work Description</span>{" "}
              <span className="fs-6">({selectedLogs.createdAt})</span>
            </ModalHeader>
            <ModalBody>
              <div
                dangerouslySetInnerHTML={{
                  __html: selectedLogs.workDescription,
                }}
              />
            </ModalBody>
          </Modal>
        </Container>
      </div>
    </>
  )
}

const TableComponent = ({
  table_data,
  handleExportToExcel,
  toggleLogsModal,
  setSelectedLogs,
}) => {
  const { checkAccess } = usePermissionData()
  const module_id = 24

  const navigate = useNavigate()

  // columns
  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
        filterable: true,
        disableFilters: true,
        className: "text-center",
        Cell: cellProps => <span>{cellProps.row.index + 1}</span>,
      },
      {
        Header: "Client Name",
        accessor: "client.clientName",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Task Name",
        accessor: "task.title",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Time Spent",
        accessor: "totalTimeSpent",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Period End",
        accessor: "periodEnd",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => {
          return (
            <span>
              {moment(cellProps.row.original.task.startDate).format("D/M/YYYY")}
            </span>
          )
        },
      },
      {
        Header: "Due Date",
        accessor: "dueDate",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => {
          return (
            <span>
              {moment(cellProps.row.original.task.dueDate).format("D/M/YYYY")}
            </span>
          )
        },
      },
      {
        Header: "Employee",
        accessor: "employee",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => {
          return (
            <div className="d-flex align-items-center ">
              <div>
                {cellProps.row.original?.employee.name}{" "}
                {cellProps.row.original?.employee.lastName}
              </div>
            </div>
          )
        },
      },
      {
        Header: "Work Type",
        accessor: "workType.workType",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Status",
        disableSort: true,
        accessor: "status",
        disableFilters: true,
        filterable: true,
        className: "text-center",
        Cell: cellProps => {
          const { colorCode, statusName } = cellProps.row.original?.status
          return (
            <div className="text-center">
              <span
                className="badge text-wrap lh-sm"
                style={{
                  color: colorCode,
                  border: `1px solid ${colorCode}`,
                  fontSize: "11px",
                }}
              >
                {statusName}{" "}
              </span>
            </div>
          )
        },
      },
      {
        Header: "End Time",
        accessor: "endTime",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Log",
        accessor: "workDescription",
        disableFilters: true,
        filterable: false,
        Cell: cellProps => (
          <div className="d-flex justify-content-center">
            <button
              title="View Logs"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setSelectedLogs({
                  createdAt: moment(cellProps.row.original.updatedAt).format(
                    "DD MMM YYYY | hh:mm A"
                  ),
                  workDescription: cellProps.row.original.workDescription,
                })
                toggleLogsModal()
              }}
            >
              <i className="mdi mdi-file-table-outline fs-5"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  )

  // table data
  const data = useMemo(() => table_data, [table_data])

  return (
    <>
      <PermittedComponent isAccess={checkAccess(module_id, "view")}>
        <CommonTable
          columns={columns}
          data={data}
          heading={"Latest Task Report"}
        >
          <div>
            <button onClick={handleExportToExcel} className="btn btn-info ms-3">
              <i className="fas fa-file-excel me-2"></i>
              Export Records
            </button>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-secondary bg-dark ms-3"
            >
              <i className="fas fa-arrow-left me-3"></i>
              Back
            </button>
          </div>
        </CommonTable>
      </PermittedComponent>
    </>
  )
}

export default ActiveTaskLastLog
